// Story 7.2 (ADO-905), AC1/AC3/AC7 — product keyword-search screen. A search
// bar (the Figma "Mega Menu - Browse" carries one) drives a ranked partial-text /
// fuzzy search, rendering the Story 7.1 ProductCard for each result row. A
// zero-results search shows an explicit empty state that ECHOES the query (AC3),
// never an error surface — a genuine transport failure is a separate Banner
// (mirrors the Browse empty-vs-error split). Add-to-basket from a result is a
// documented seam only (AC7): ProductCard renders no basket write here.
//
// Presentational + data-fetching only; the auth gate lives on the /search route
// in main.tsx (mirrors BrowseRoute). Search is submit-driven (Enter / button) so
// each result set corresponds to a query the user deliberately ran. The last
// good result set is KEPT visible through a re-search and through a transient
// failure (the browse "keep listing during refetch" posture, 0cff272); a
// request-sequence guard drops out-of-order settlements so a slow response can
// never overwrite a newer one.
//
// Epic 7 pixel parity (ADO-903): the results body wears the SAME re-skinned
// listing chrome as CategoryListing — pinned CollapsibleFilterSidebar rail
// (with ad placeholder), toolbar row (Filters switch / drawer button ·
// breadcrumb-style Shop / “term” crumb · count + grid/list view toggle), chips
// row, and the shared mobile FiltersDrawer. The listing chrome CSS is imported
// from CategoryListing.module.css so the two surfaces stay in lock-step.
// Search is a single capped page (no cursor), so it renders NO pagination
// footer; the Story 7-FU-C truncation notice (AC6) keeps its exact text +
// role="status" semantics, restyled as a subtle inline note.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  AppShell,
  Banner,
  Button,
  CollapsibleFilterSidebar,
  FormField,
  Icon,
} from '../../design-system';
import {
  MAX_SEARCH_QUERY_LENGTH,
  searchProducts,
  type ProductSearchResult,
} from '../../api/search.client';
import {
  hasActiveFilters,
  removeFilterValue,
  toggleFilterValue,
  type FacetKey,
  type ProductFilters,
} from '../../api/facets';
import { useFilterPanel } from '../../api/useFilterPanel';
import { useAvailability } from '../../availability/useAvailability';
import { usePrices } from '../../pricing/usePriceStore';
import { useCatalogueEntryKey } from '../../ordering/catalogueEntry';
import { useOnOrder } from '../../onOrder/useOnOrder';
import { DepotPicker } from '../../availability/DepotPicker';
import { FilterChips } from '../Browse/FilterChips';
import { FilterPanel } from '../Browse/FilterPanel';
import { FiltersDrawer } from '../Browse/FiltersDrawer';
import { ProductCard } from '../Browse/ProductCard';
import { useViewMode } from '../Browse/useViewMode';
// Shared listing chrome (shell / sidebar / toolbar / chips / grid / list) —
// imported from CategoryListing so search results and the category listing
// cannot drift apart visually (Epic 7 pixel parity, ADO-903).
import listing from '../Browse/CategoryListing.module.css';
import crumbs from '../Browse/Breadcrumb.module.css';
import styles from './ProductSearch.module.css';

type Phase = 'idle' | 'loading' | 'error' | 'ready';

// Story 7-FU-D (ADO-1217), AC3 — mark the first row of result cards as the LCP
// candidate (eager + fetchpriority=high); every other card stays lazy. A small
// constant ≈ one visual row (D-3); real LCP timing is a Playwright/manual concern.
const EAGER_IMAGE_COUNT = 4;

export interface ProductSearchProps {
  /**
   * Epic 7 re-skin (ADO-903) — when true, render only the results body (no
   * AppShell, no internal search form): the `/products` SiteHeader owns the
   * search box and drives the committed term via `term`. Default (page mode)
   * preserves the standalone screen used by the existing tests.
   */
  readonly embedded?: boolean;
  /** The committed search term when embedded (from the SiteHeader search box). */
  readonly term?: string;
  /**
   * A monotonic token that changes on every submit (even for an identical term),
   * so re-submitting the same query forces a re-run — page-mode parity with
   * `submitNonce`. Only meaningful when `embedded`.
   */
  readonly runToken?: number;
}

export function ProductSearch({ embedded = false, term, runToken }: ProductSearchProps = {}) {
  const [query, setQuery] = useState('');
  // Tracks the last embedded term so filters reset ONLY on a term change (page-mode
  // parity) — an identical re-submit (runToken bump) keeps the active filters.
  const prevEmbeddedTerm = useRef<string | null>(null);
  // The last successfully loaded result set (carries the echoed query for AC3).
  // Retained through 'loading' and 'error' so a re-search or a transient
  // failure never blanks a result set the user is looking at.
  const [result, setResult] = useState<ProductSearchResult | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  // Story 7.10 (ADO-906): the term that produced the current result (so a filter
  // toggle can re-run the SAME search with the new filters, AC1/AC3), the active
  // attribute filters, a submit nonce (forces a re-search on re-submitting the
  // same term), and the panel toggle (desktop rail) + drawer state (<1024px).
  const [submittedTerm, setSubmittedTerm] = useState('');
  const [submitNonce, setSubmitNonce] = useState(0);
  const [filters, setFilters] = useState<ProductFilters>({});
  // Enabler E7-C (ADO-2274), D-1: the selected ordering entry rides the search: the server
  // searches that entry's range, and shows a discontinued exact hit (Story 8.2, ADO-2011, AC6)
  // only when it has a live substitute at the entry's depot. An entry change re-runs the search.
  const catalogueEntryKey = useCatalogueEntryKey();
  const { showFilters, toggleFilters } = useFilterPanel();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Epic 7 re-skin (ADO-903): grid/list view toggle, opening in the grid view at
  // every width (shared with CategoryListing).
  const { viewMode, selectView } = useViewMode();
  // Monotonic submit counter — a settlement whose sequence is no longer current
  // is stale (a newer submit/filter-change superseded it) and is discarded.
  const seqRef = useRef(0);
  // Story 7-FU-D (ADO-1217), AC2 — the in-flight request's controller, so a new
  // run (or an unmount) can ABORT the previous request on the wire rather than
  // letting N superseded searches run to completion server-side.
  const abortRef = useRef<AbortController | null>(null);

  // The single search runner (AC1/AC3): the raw FTS candidate set is filtered +
  // faceted server-side; the seq guard drops out-of-order settlements so a slow
  // response can never overwrite a newer one. Driven ONLY by the effect below so
  // submit and filter-change share one path.
  useEffect(() => {
    if (submittedTerm === '') return;
    // With no entry there is nothing to search; the effect re-runs when one is chosen. The products
    // page keys its embedded search by the entry, so there it remounts empty; this branch drops
    // the previous range's results for a search that stays mounted (page mode).
    if (catalogueEntryKey === null) {
      ++seqRef.current;
      abortRef.current?.abort();
      setResult(null);
      setPhase('idle');
      return;
    }
    const seq = ++seqRef.current;
    // AC2 — abort the previous in-flight request before starting a new one, then
    // arm a fresh controller for this run. The effect cleanup aborts too (covers
    // unmount + a dep-change supersede); the seq guard below stays the
    // belt-and-braces for any settlement that still resolves after the abort.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase('loading');
    searchProducts(catalogueEntryKey, submittedTerm, filters, controller.signal)
      .then((next) => {
        if (seq !== seqRef.current) return; // stale response — a newer search ran
        setResult(next);
        setPhase('ready');
      })
      .catch((err: unknown) => {
        // AC2 — an aborted request is NOT a failure: it was superseded or the
        // screen unmounted. Never trip the error Banner (reserved for a genuine
        // transport/non-2xx failure); leave the current phase to the newer run.
        if (controller.signal.aborted || (err as { name?: string } | null)?.name === 'AbortError') {
          return;
        }
        if (seq !== seqRef.current) return; // stale failure — keep the newer state
        setPhase('error'); // keep the last good `result` visible under the banner
      });
    return () => {
      controller.abort();
    };
  }, [submittedTerm, filters, submitNonce, catalogueEntryKey]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const term = query.trim();
    if (term === '') return; // a blank submit is a no-op — nothing to search
    // Story 7.10 code-review (2026-07-13): reset attribute filters ONLY on a NEW
    // search term (mirrors Browse's per-node reset). Otherwise a stale filter from
    // the previous term is applied to the new one — an empty result for a term that
    // actually has matches, plus an orphan chip the recomputed facets can't offer.
    // Guarded on term change so re-submitting the SAME query (Enter again to
    // refresh) keeps the active filters rather than silently wiping them.
    if (term !== submittedTerm) setFilters({});
    setSubmittedTerm(term);
    setSubmitNonce((n) => n + 1); // re-run even when the term is unchanged
  };

  // Epic 7 re-skin (ADO-903) — embedded mode: the SiteHeader owns the search box,
  // so the committed `term` prop drives the SAME runner the page-mode form does.
  // Keyed on `runToken` too, so re-submitting the IDENTICAL term still re-runs
  // (page-mode `submitNonce` parity). Filters reset ONLY when the term actually
  // changed (mirrors onSubmit's per-term reset); a filter toggle (term unchanged,
  // no new submit) never fires this effect and so never wipes the active filters.
  useEffect(() => {
    if (!embedded) return;
    const t = (term ?? '').trim();
    setSubmittedTerm(t);
    if (t !== prevEmbeddedTerm.current) {
      setFilters({});
      prevEmbeddedTerm.current = t;
    }
    // Force the runner effect to re-fire even when `submittedTerm` is unchanged
    // (identical re-submit) — mirrors page-mode's submit-nonce bump.
    setSubmitNonce((n) => n + 1);
  }, [embedded, term, runToken]);

  // Story 7.3 (ADO-1139): populate the availability store (stock pill +
  // "{n} in store") for the result rows on screen; no-op when unconfigured.
  // Story 8.2 (ADO-2011), AC6: a `notAvailable` (discontinued exact hit) row has no
  // live stock to show, so it is left out of these reads.
  const liveSkus = (result?.products ?? [])
    .filter((p) => p.notAvailable !== true)
    .map((p) => p.sku);
  useAvailability(liveSkus);
  // Pricing migration 17 (ADO-2248): one price request for the same products (no-op unless
  // VITE_REAL_PRICES_ENABLED is on).
  usePrices(liveSkus);
  // Story 8.12 (ADO-2021): which of them the Customer already has on order, one batched read.
  useOnOrder(liveSkus);

  const filtersActive = hasActiveFilters(filters);
  const toggleValue = (key: FacetKey, value: string) =>
    setFilters((f) => toggleFilterValue(f, key, value));
  const clearAll = () => setFilters({});
  const removeChip = (key: FacetKey, value: string) =>
    setFilters((f) => removeFilterValue(f, key, value));

  // The count caption survives an error too — the retained grid/empty-state
  // below the banner must keep saying WHICH kind of set it belongs to (the
  // toolbar crumb carries the term itself). Zero results still echo the query.
  const statusText =
    phase === 'loading'
      ? 'Searching…'
      : (phase === 'ready' || phase === 'error') && result !== null
        ? result.isEmpty
          ? `No results for “${result.query}”`
          : `Showing ${result.products.length} ${
              result.products.length === 1 ? 'result' : 'results'
            }`
        : '';

  const filterPanel = (
    <FilterPanel
      facets={result?.facets ?? []}
      selected={filters}
      onToggleValue={toggleValue}
      onClearAll={clearAll}
      hasActive={filtersActive}
    />
  );

  const content = (
    <div className={styles.search}>
      {!embedded && (
        <form className={styles.searchBar} role="search" onSubmit={onSubmit}>
          <FormField
            label="Search products"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, description or SKU…"
            maxLength={MAX_SEARCH_QUERY_LENGTH}
          />
          <Button type="submit">Search</Button>
        </form>
      )}

      {/* Re-skinned listing shell (ADO-903): pinned filters rail on the LEFT
          (desktop, same CollapsibleFilterSidebar + ad placeholder as the
          category listing), results content on the RIGHT. */}
      <div className={listing.shell}>
        <CollapsibleFilterSidebar open={showFilters} className={listing.sidebar} showAdPlaceholder>
          <div id="search-filter-panel">{filterPanel}</div>
        </CollapsibleFilterSidebar>

        <div className={listing.content}>
          {/* Toolbar (prototype "Listing options" row): Filters switch (desktop) /
              Filters drawer button (<1024) · breadcrumb-style Shop / “term” crumb ·
              result count + grid/list view toggle. */}
          <div className={listing.toolbar} role="group" aria-label="Listing options">
            <button
              type="button"
              className={listing.filtersToggle}
              aria-expanded={showFilters}
              aria-controls="search-filter-panel"
              onClick={toggleFilters}
            >
              Filters
              <span
                className={`${listing.filtersSwitch} ${showFilters ? listing.filtersSwitchOn : ''}`}
                aria-hidden
              >
                <span className={listing.filtersKnob} />
              </span>
            </button>
            <button
              type="button"
              className={listing.drawerButton}
              aria-haspopup="dialog"
              onClick={() => setDrawerOpen(true)}
            >
              <Icon name="sliders" size={20} />
              Filters
            </button>

            <div className={listing.toolbarCrumb}>
              {submittedTerm !== '' && (
                // Breadcrumb-style Shop / “term” crumb (prototype search-results
                // chrome). "Shop" is a non-link crumb here — leaving the search is
                // the header's Shop link/Clear control, not this trail.
                <nav aria-label="Breadcrumb" className={crumbs.breadcrumb}>
                  <ol className={crumbs.list}>
                    <li className={crumbs.item}>
                      <span className={crumbs.current}>Shop</span>
                    </li>
                    <li className={`${crumbs.item} ${crumbs.itemCurrent}`}>
                      <Icon name="caretRight" size={16} className={crumbs.separator} />
                      <span className={crumbs.current} aria-current="page">
                        “{submittedTerm}”
                      </span>
                    </li>
                  </ol>
                </nav>
              )}
            </div>

            <div className={listing.toolbarRight}>
              {/* The ONE live region on the page (besides the error Banner's own
                  role="alert") — persistently mounted so screen readers reliably
                  announce its text changes; the empty-state box below is visual only. */}
              <p className={listing.count} role="status">
                {statusText}
              </p>
              <div className={listing.viewToggle} role="group" aria-label="View mode">
                <button
                  type="button"
                  className={`${listing.viewButton} ${viewMode === 'grid' ? listing.viewButtonActive : ''}`}
                  aria-label="Grid view"
                  aria-pressed={viewMode === 'grid'}
                  onClick={() => selectView('grid')}
                >
                  <Icon name="gridView" size={20} />
                </button>
                <button
                  type="button"
                  className={`${listing.viewButton} ${viewMode === 'list' ? listing.viewButtonActive : ''}`}
                  aria-label="List view"
                  aria-pressed={viewMode === 'list'}
                  onClick={() => selectView('list')}
                >
                  <Icon name="listView" size={20} />
                </button>
              </div>
            </div>
          </div>

          <FilterChips
            filters={filters}
            onRemove={removeChip}
            onClearAll={clearAll}
            className={listing.chipsRow}
          />

          {/* Story 7-FU-C (ADO-1216), AC6 — when the result set was capped by the
              server search limit, say so honestly instead of presenting the shown
              rows as the complete set. Only meaningful once a result is on screen
              (ready/error keep the last good result); never on the loading/idle path.
              Gated on `!isEmpty`: `truncated` reflects the raw DB cap BEFORE the
              in-memory facet filter (AC4), so an active facet can empty the display
              while `truncated` stays true — showing this notice beside the "No
              results" empty state would contradict it. The copy is count-free: the
              client has only the `truncated` boolean, not the exact cap (a `total`
              is deferred — D-4/OQ-4), so it must not assert "the first N matches"
              (N would be the post-facet displayed count, not the cap). */}
          {(phase === 'ready' || phase === 'error') &&
            result !== null &&
            result.truncated &&
            !result.isEmpty && (
              <p className={styles.truncationNotice} role="status">
                There are more matches than shown here — refine your search to narrow it down.
              </p>
            )}

          <div className={listing.main}>
            {phase === 'idle' && !embedded && (
              <p className={styles.prompt}>
                Start typing a product name, description or SKU to search.
              </p>
            )}

            {phase === 'error' && (
              <Banner type="error">Couldn&rsquo;t run your search. Please try again.</Banner>
            )}

            {result !== null && result.isEmpty && (
              // AC3/AC4 — explicit empty state echoing the query, not an error.
              // When filters are active, offer a clear-filters affordance — and
              // say the FILTERS excluded the matches (the term itself did match),
              // mirroring Browse's dedicated title (code-review 2026-07-13).
              <div className={styles.empty}>
                <p className={styles.emptyTitle}>
                  {filtersActive
                    ? `No products for “${result.query}” match the selected filters.`
                    : `No products match “${result.query}”.`}
                </p>
                <p className={styles.emptyHint}>
                  {filtersActive
                    ? 'Try removing a filter to widen your results.'
                    : 'Try a different spelling, fewer words, or a product code.'}
                </p>
                {filtersActive && (
                  <Button variant="secondary" onClick={clearAll}>
                    Clear filters
                  </Button>
                )}
              </div>
            )}

            {result !== null && !result.isEmpty && (
              <ul className={viewMode === 'list' ? listing.list : listing.grid}>
                {result.products.map((product, index) => (
                  <li key={product.id}>
                    {/* Story 7.9 (ADO-1141) filled the 7.2-AC7 add-to-basket seam:
                        the shared ProductCard now carries the one-click "Add to Basket"
                        button (optimistic client store; durable write is Story 8.2, D-1). */}
                    <ProductCard
                      product={product}
                      priority={index < EAGER_IMAGE_COUNT}
                      layout={viewMode}
                    />
                  </li>
                ))}
              </ul>
            )}

            {/* No pagination footer: search is a single capped page (cursor paging
                is a Browse concern; the truncation notice above covers the cap). */}
          </div>
        </div>
      </div>

      {/* Mobile/tablet (<1024px) filters drawer — the SAME panel, in the shared
          left drawer (prototype parity with the category listing). */}
      <FiltersDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {filterPanel}
      </FiltersDrawer>
    </div>
  );

  // Embedded under the /products SiteHeader (which owns the search box + depot
  // switcher): return just the results body. Page mode keeps the AppShell chrome.
  if (embedded) return content;

  return (
    <AppShell
      title="Search"
      subtitle="Find products by name, description or SKU."
      actions={<DepotPicker />}
    >
      {content}
    </AppShell>
  );
}

export default ProductSearch;
